import React, { ReactElement } from "react";
import Image from "next/image";
import styles from "./styles.module.scss";

export default function ProgressBar({
    total,
    current,
    className,
    color,
    backgroundColor,
    shift = 0,
    showStepNumberOnly = false,
    mode = "continuous",
    title = "",
    titleIcon,
    activeSegmentClassName = "",
}: {
    total: number;
    current: number;
    className: string;
    color: string | undefined;
    backgroundColor?: string | undefined;
    shift?: number;
    showStepNumberOnly?: boolean;
    mode?: "continuous" | "steps";
    title?: string;
    titleIcon?: string;
    activeSegmentClassName?: string;
}): ReactElement {
    const progress = (current + shift) / (total + shift - 1);

    const progressPercentage = Math.round(progress * 100);
    const hideStepsNumber = current + shift + 1 > total + shift - 1;

    if (mode === "steps") {
        const activeIndex = current + shift;

        return (
            <div
                className={`${styles["top-progress-bar"]} ${className} ${
                    activeIndex >= total - 1 ? styles["success"] : ""
                }`}
                style={{ backgroundColor }}
            >
                {(title || titleIcon) && (
                    <div className={styles["progress-title"]}>
                        {titleIcon && (
                            <div className={styles["title-icon"]}>
                                <Image
                                    src={titleIcon}
                                    alt={title ?? "form icon"}
                                    height="50px"
                                    width="50px"
                                />
                            </div>
                        )}
                        {title && (
                            <div className={styles["title"]}>{title}</div>
                        )}
                    </div>
                )}

                <div className={styles["segments"]}>
                    {Array.from({ length: total }).map((_, index) => {
                        const isActive = index <= activeIndex;

                        return (
                            <div
                                key={index}
                                className={`${styles["segment"]} ${
                                    isActive
                                        ? `${styles["active-segment"]} progressStepActive ${activeSegmentClassName}`
                                        : styles["inactive-segment"]
                                }`}
                            />
                        );
                    })}
                </div>
            </div>
        );
    }

    return (
        <div
            className={`${styles["progress-bar"]} ${className} ${
                progressPercentage === 100 ? styles["success"] : ""
            }`}
        >
            {!showStepNumberOnly ? (
                <>
                    <div
                        style={{
                            left: progressPercentage
                                ? `${progressPercentage}%`
                                : 0,
                            transform: "translateX(-50%)",
                            background: color,
                        }}
                        className={styles["percentage"]}
                    >
                        <span className="text-xs">{progressPercentage}%</span>
                        <span
                            style={{ borderBottomColor: color }}
                            className={styles["arrow"]}
                        />
                    </div>
                    <div className={styles["bar"]}>
                        <span
                            style={{
                                width: `${progressPercentage}%`,
                                background: color,
                            }}
                        />
                    </div>
                </>
            ) : (
                <>
                    {!hideStepsNumber && (
                        <p className={styles["step-number"]}>
                            {current + shift + 1} of {total + shift - 1}
                        </p>
                    )}
                </>
            )}
        </div>
    );
}
